import moment from 'moment'
import { timeFormat, TRAINING, VACCINE_LABELS } from '../utils'

const REQUIRED_EXPIRED_VACCINES_STATUS = ['In Progress', 'Approved', 'Pending']

export const isExpired = (vaccine) => {
	if (!vaccine || !vaccine.expiration_date) {
		return true
	}
	const expirationEndOfDay = moment(vaccine.expiration_date).utc().endOf('day')
	const nowEndOfDay = moment().utc().endOf('day')
	if (expirationEndOfDay.isBefore(nowEndOfDay)) {
		return true
	}

	return false
}

export const getAge = (pet) => {
	if (!pet.birthday) return 0

	const days = moment(pet.birthday).startOf('day').diff(moment().endOf('day'), 'days')

	return Math.abs(days)
}

export const getDogIssues = (pet, visit = {}) => {
	try {
		const { type = null, startDate = null, endDate = null, status } = visit
		if (!pet) return []
		const issues = []
		const badVaccines = VACCINE_LABELS.some((vax) => {
			const findVax =
				pet && pet.vaccines && pet.vaccines.find((v) => v.type === vax.name || v.name === vax.name)

			return !findVax || isExpired(findVax) || !findVax.valid
		})

		if (badVaccines)
			issues.push({
				message: `Unverified Vaccines`,
				type: 'danger'
			})

		const referenceDate = endDate || startDate
		const hasValidExpiredStatus =
			status && status?.name && REQUIRED_EXPIRED_VACCINES_STATUS.includes(status?.name)
		if (referenceDate && !badVaccines && hasValidExpiredStatus) {
			const expiredVaccines = pet.vaccines.some((vax) => {
				const willExpire =
					vax.valid &&
					moment().isBefore(vax.expiration_date) &&
					moment(referenceDate).isSameOrAfter(vax.expiration_date, 'date')
				return willExpire
			})

			if (expiredVaccines)
				issues.push({
					message: 'Vaccines Expire Before Appt. End',
					type: 'danger'
				})
		}
		const age_in_days = getAge(pet)

		const valid_sexes = ['Neutered Male', 'Spayed Female']

		// Exclude min age check for training as we may have puppy classes
		if (age_in_days < 112 && type !== TRAINING) {
			issues.push({
				message: `${pet.name} is under the age requirement.`,
				type: 'danger'
			})
		} else if (age_in_days > 180 && !valid_sexes.includes(pet.sex)) {
			if (pet.spay_neuter_deadline && moment().isBefore(pet.spay_neuter_deadline) && type !== 'grooming') {
				issues.push({
					message: `Spay & Neutered Exemption ${moment(pet.spay_neuter_deadline).diff(
						pet.birthday,
						'months'
					)} Months`,
					color: '#CC8D33',
					type: 'warning'
				})
			} else if (
				(!pet.spay_neuter_deadline ||
					(pet.spay_neuter_deadline && moment().isAfter(pet.spay_neuter_deadline))) &&
				type !== 'grooming'
			) {
				issues.push({
					message: `${pet && pet.name ? pet.name.trim() : 'Dog'} must be spayed or neutered.`,
					type: 'danger'
				})
			}
		}

		return issues
	} catch (e) {
		console.log('badon here...', e)

		return []
	}
}

export const getDepartingInfo = (data, prms) => {
	let { headingText, timeText, date, pickUpDeadline } = prms
	let isExtendedVisit = data.extended_pickup
	let hasDepartingPets = false
	if (moment(data.endDate).isSame(date, 'date') && data?.pet_status?.some((s) => s.checked_in)) {
		hasDepartingPets = true
	}

	if (hasDepartingPets) {
		headingText = 'Departing AM'
		if (isExtendedVisit) {
			headingText = 'Departing'
			pickUpDeadline = '21:00'
		} else if (data.time_block === 'afternoon') {
			headingText = 'Departing'
			pickUpDeadline = '19:00'
		} else if (data.time_block === 'evening') {
			headingText = 'Departing'
			pickUpDeadline = '21:00'
		} else if (pickUpDeadline === '19:00') {
			headingText = 'Departing'
		}
		timeText = pickUpDeadline ? `By ${moment(pickUpDeadline, 'HH:mm').format(timeFormat(pickUpDeadline))}` : ''
	}

	return {
		headingText,
		timeText,
		hasDepartingPets
	}
}

export const isPetsVaccinesValidForFacility = (pet, facility) => {
	const required_vaccines = facility.vaccines_required
	const pet_vaccines = pet.vaccines || []

	if (pet_vaccines && pet_vaccines.length) {
		return required_vaccines
			.map((vaccine) => {
				const match = pet_vaccines.filter(
					(pet_vaccine) => pet_vaccine.type === vaccine.name || pet_vaccine.type === vaccine.type
				)
				if (match && match.length) {
					if (
						match[0].expiration_date &&
						!moment(match[0].expiration_date).isBefore(moment()) &&
						match[0].valid
					) {
						// has vaccine and is valid
						return true
					}

					// has matched vaccine but is not valid
					return false
				}

				// does not have vaccine records that matches facility's required vaccines
				return false
			})
			.every((v) => v)
	}

	return false
}

export const getVisitDateString = (visit) => {
	if (visit) {
		const start = visit.startDate ? moment(visit.startDate) : null
		const end = visit.type === 'boarding' && visit.endDate ? moment(visit.endDate) : moment(visit.startDate)

		if (start && end) {
			const startDay = start.format('D')
			const endDay = end.format('D')
			const startMonth = start.format('MMM')
			const endMonth = end.format('MMM')

			let endDateDisplay = ''

			if (visit.type === 'boarding') {
				endDateDisplay =
					startMonth === endMonth && endDay
						? ` - ${endDay}`
						: startMonth !== endMonth && endDay
						? ` - ${endMonth} ${endDay}`
						: null
			}

			return `${startMonth} ${startDay}${startMonth !== endMonth ? endMonth : ''}${endDateDisplay}`
		} else {
			return null
		}
	} else {
		return null
	}
}

export const getAvatarLetters = (name) => {
	if (!name || typeof name !== 'string') {
		return null
	}

	const splitName = name.split(' ')

	if (splitName.length > 1) {
		return splitName.map((name) => name.substring(0, 1)).join('')
	} else {
		return splitName[0].substring(0, 1)
	}
}

export const enumerateDaysBetweenDates = (startDate, endDate) => {
	const dates = []

	const currDate = moment(startDate).startOf('day')
	const lastDate = moment(endDate).endOf('day')

	dates.push(currDate.clone().format('YYYY-MM-DD'))

	while (currDate.add(1, 'days').diff(lastDate) < 0) {
		dates.push(currDate.clone().format('YYYY-MM-DD'))
	}

	return dates
}

export const getName = (firstName, lastName) => {
	return firstName && !lastName
		? `${firstName}`
		: !firstName && lastName
		? `${lastName}`
		: `${firstName} ${lastName}`
}

export const matchPetsToVisit = (visit, pets) => {
	if (visit && visit.pet_ids.length > 0 && pets) {
		visit.pets = visit.pet_ids.map((pet_id) => {
			const pet = pets.find((pet) => pet._id === pet_id)
			let status
			let add_on_services = []
			if (pet) {
				status =
					(visit && visit.pet_status && visit.pet_status.find((status) => status.pet_id === pet._id)) ||
					{}
				add_on_services = visit.add_on_services.filter(
					(add_on_service) => add_on_service.pet_id === pet._id
				)
			}

			return {
				...pet,
				status: status ? { ...status } : {},
				add_on_services
			}
		})

		return visit
	} else {
		return {
			...visit,
			pets: []
		}
	}
}

export const getChipStyles = (type, formatLength = false) => {
	const success = ['success', 'in progress', 'checked in', 'delivered', 'active', 'yes']
	const warning = ['change requested', 'warning', 'pending']
	const failure = ['rejected', 'cancelled', 'canceled', 'no show', 'no_show', 'disabled', 'no']
	const purple = ['checkin-guests', 'approved', 'overflow', 'purple', 'paused']

	if (typeof type === 'string' && success.includes(type.toLowerCase())) {
		return {
			color: '#2E7B32',
			backgroundColor: 'rgba(76, 175, 80, 0.1)',
			textTransform: 'uppercase',
			fontWeight: 800,
			...(formatLength && {
				width: '90%',
				overflow: 'hidden',
				whiteSpace: 'nowrap',
				justifyContent: 'flex-start'
			})
		}
	} else if (typeof type === 'string' && failure.includes(type.toLowerCase())) {
		return {
			color: '#D32F2F',
			backgroundColor: 'rgba(235, 76, 76, 0.1)',
			textTransform: 'uppercase',
			fontWeight: 800,
			...(formatLength && {
				width: '90%',
				overflow: 'hidden',
				whiteSpace: 'nowrap',
				justifyContent: 'flex-start'
			})
		}
	} else if (typeof type === 'string' && warning.includes(type.toLowerCase())) {
		return {
			color: '#B26200',
			background: 'rgba(249, 168, 37, 0.1)',
			textTransform: 'uppercase',
			fontWeight: 800,
			...(formatLength && {
				width: '90%',
				overflow: 'hidden',
				whiteSpace: 'nowrap',
				justifyContent: 'flex-start'
			})
		}
	} else if (typeof type === 'string' && purple.includes(type.toLowerCase())) {
		return {
			color: '#6B4AD6',
			background: 'rgba(107, 74, 214, 0.1)',
			textTransform: 'uppercase',
			fontWeight: 800,
			...(formatLength && {
				width: '90%',
				overflow: 'hidden',
				whiteSpace: 'nowrap',
				justifyContent: 'flex-start'
			})
		}
	} else {
		return {
			// color: '#6B4AD6',
			// background: 'rgba(107, 74, 214, 0.1)',
			textTransform: 'uppercase',
			fontWeight: 800,
			...(formatLength && {
				width: '90%',
				overflow: 'hidden',
				whiteSpace: 'nowrap',
				justifyContent: 'flex-start'
			})
		}
	}
}

export const getPetSize = (weight, length) => {
	if (weight <= 12) {
		return length === 's' ? 'XS' : 'X-Small'
	} else if (weight >= 13 && weight < 31) {
		return length === 's' ? 'S' : 'Small'
	} else if (weight >= 31 && weight < 48) {
		return length === 's' ? 'M' : 'Medium'
	} else if (weight >= 48 && weight < 70) {
		return length === 's' ? 'L' : 'Large'
	} else if (weight >= 70) {
		return length === 's' ? 'XL' : 'X-Large'
	} else {
		return null
	}
}

export const getPetSizeV1 = (pet, uppercase = false) => {
	if (pet.height) {
		if (uppercase) return pet.height.toUpperCase()
		else return pet.height
	}
	let text = ''
	if (pet.weight <= 12) {
		text = 'xs'
	} else if (pet.weight >= 13 && pet.weight < 31) {
		text = 's'
	} else if (pet.weight >= 31 && pet.weight < 48) {
		text = 'm'
	} else if (pet.weight >= 48 && pet.weight < 71) {
		text = 'l'
	} else if (pet.weight >= 71) {
		text = 'xl'
	} else {
		text = null
	}

	if (text && uppercase) return text.toUpperCase()

	return text
}

export const getPetAge = (birthday) => {
	if (birthday) {
		const difference = moment().diff(moment(birthday), 'y')
		if (difference < 1) {
			return 'Puppy'
		} else if (difference < 2) {
			return 'Young'
		} else if (difference < 7) {
			return 'Adult'
		} else if (difference >= 7) {
			return 'Senior'
		}
	} else {
		return null
	}
}

export const getPetAgeNumber = (birthday) => {
	if (birthday) {
		const difference = moment().diff(moment(birthday), 'y')

		if (difference < 10) {
			return `${difference || 1}`
		} else if (difference >= 10) {
			return '10+'
		}
	} else {
		return null
	}
}

export const states = [
	{ id: 1, name: 'Alabama', abbreviation: 'AL' },
	{ id: 2, name: 'Alaska', abbreviation: 'AK' },
	{ id: 3, name: 'Arizona', abbreviation: 'AZ' },
	{ id: 4, name: 'Arkansas', abbreviation: 'AR' },
	{ id: 5, name: 'California', abbreviation: 'CA' },
	{ id: 6, name: 'Colorado', abbreviation: 'CO' },
	{ id: 7, name: 'Connecticut', abbreviation: 'CT' },
	{ id: 8, name: 'Delaware', abbreviation: 'DE' },
	{ id: 9, name: 'Florida', abbreviation: 'FL' },
	{ id: 10, name: 'Georgia', abbreviation: 'GA' },
	{ id: 11, name: 'Hawaii', abbreviation: 'HI' },
	{ id: 12, name: 'Idaho', abbreviation: 'ID' },
	{ id: 13, name: 'Illinois', abbreviation: 'IL' },
	{ id: 14, name: 'Indiana', abbreviation: 'IN' },
	{ id: 15, name: 'Iowa', abbreviation: 'IA' },
	{ id: 16, name: 'Kansas', abbreviation: 'KS' },
	{ id: 17, name: 'Kentucky', abbreviation: 'KY' },
	{ id: 18, name: 'Louisiana', abbreviation: 'LA' },
	{ id: 19, name: 'Maine', abbreviation: 'ME' },
	{ id: 20, name: 'Maryland', abbreviation: 'MD' },
	{ id: 21, name: 'Massachusetts', abbreviation: 'MA' },
	{ id: 22, name: 'Michigan', abbreviation: 'MI' },
	{ id: 23, name: 'Minnesota', abbreviation: 'MN' },
	{ id: 24, name: 'Mississippi', abbreviation: 'MS' },
	{ id: 25, name: 'Missouri', abbreviation: 'MO' },
	{ id: 26, name: 'Montana', abbreviation: 'MT' },
	{ id: 27, name: 'Nebraska', abbreviation: 'NE' },
	{ id: 28, name: 'Nevada', abbreviation: 'NV' },
	{ id: 29, name: 'New Hampshire', abbreviation: 'NH' },
	{ id: 30, name: 'New Jersey', abbreviation: 'NJ' },
	{ id: 31, name: 'New Mexico', abbreviation: 'NM' },
	{ id: 32, name: 'New York', abbreviation: 'NY' },
	{ id: 33, name: 'North Carolina', abbreviation: 'NC' },
	{ id: 34, name: 'North Dakota', abbreviation: 'ND' },
	{ id: 35, name: 'Ohio', abbreviation: 'OH' },
	{ id: 36, name: 'Oklahoma', abbreviation: 'OK' },
	{ id: 37, name: 'Oregon', abbreviation: 'OR' },
	{ id: 38, name: 'Pennsylvania', abbreviation: 'PA' },
	{ id: 39, name: 'Rhode Island', abbreviation: 'RI' },
	{ id: 40, name: 'South Carolina', abbreviation: 'SC' },
	{ id: 41, name: 'South Dakota', abbreviation: 'SD' },
	{ id: 42, name: 'Tennessee', abbreviation: 'TN' },
	{ id: 43, name: 'Texas', abbreviation: 'TX' },
	{ id: 44, name: 'Utah', abbreviation: 'UT' },
	{ id: 45, name: 'Vermont', abbreviation: 'VT' },
	{ id: 46, name: 'Virginia', abbreviation: 'VA' },
	{ id: 47, name: 'Washington', abbreviation: 'WA' },
	{ id: 48, name: 'West Virginia', abbreviation: 'WV' },
	{ id: 49, name: 'Wisconsin', abbreviation: 'WI' },
	{ id: 50, name: 'Wyoming', abbreviation: 'WY' },
	{ id: 51, name: 'District of Columbia', abbreviation: 'DC' }
]

export const isEmailValid = (email) => {
	if (typeof email === 'string') {
		return email.match(
			/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
		)
	}
}

export const isHoliday = (ourDay, vacationDates) => {
	const find = vacationDates.find((d) => {
		const start = moment(d.startDate).startOf('day')
		const end = moment(d.endDate).endOf('day')

		return ourDay.isBetween(start, end, null, []) && !d.type
	})

	return find ? true : false
}

export const colors = [
	'Apricot',
	'Black',
	'Blue',
	'Brindle',
	'Brown',
	'Chestnut',
	'Chocolate',
	'Cream',
	'Dark Brown',
	'Fawn',
	'Golden',
	'Gray',
	'Light Brown',
	'Liver',
	'Merle',
	'Orange',
	'Parti-Color',
	'Phantom',
	'Red',
	'Rust',
	'Sable',
	'Silver',
	'Spotted',
	'Tan',
	'Tri-Colored',
	'Wheaten',
	'White',
	'Yellow'
]

export const difference = (o1, o2) => {
	let k
	let kDiff
	const diff = {}
	for (k in o1) {
		if (!o1.hasOwnProperty(k)) {
		} else if (typeof o1[k] != 'object' || typeof o2[k] != 'object') {
			if (!(k in o2) || o1[k] !== o2[k]) {
				diff[k] = o2[k]
			}
		} else if ((kDiff = difference(o1[k], o2[k]))) {
			diff[k] = kDiff
		}
	}
	for (k in o2) {
		if (o2.hasOwnProperty(k) && !(k in o1)) {
			diff[k] = o2[k]
		}
	}
	for (k in diff) {
		if (diff.hasOwnProperty(k)) {
			return diff
		}
	}

	return false
}
